/**
 * Hook centralizado para carregar qualidades por categoria
 *
 * Encapsula fetchQualities com cache via useApiCache.
 * Inclui debounce automático para evitar requisições múltiplas rápidas.
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { useApiCache } from "../../../lib/useApiCache";
import type { Quality } from "../../../types/quotation";

const QUALITIES_ENDPOINT_BASE = "/categoria/one1-outros";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos
const DEBOUNCE_MS = 300; // 300ms de debounce

export interface UseQualitiesOptions {
	/** ID da categoria (obrigatório) */
	categoriaId: number | null;
	/** Se true, desabilita debounce (útil para requisições manuais) */
	disableDebounce?: boolean;
}

export interface UseQualitiesResult {
	/** Qualidades da categoria */
	qualities: Quality[];
	/** Indica se está carregando */
	loading: boolean;
	/** Mensagem de erro (null se não houver erro) */
	error: string | null;
	/** Código do erro para tratamento específico */
	errorCode: "network" | "validation" | "auth" | "unknown" | null;
	/** Força refresh dos dados (ignora cache) */
	refresh: () => Promise<void>;
	/** Indica se já tentou carregar pelo menos uma vez */
	hasAttempted: boolean;
}

/**
 * Hook para carregar qualidades de uma categoria da API
 *
 * @example
 * ```tsx
 * const { qualities, loading, error } = useQualities({ categoriaId: 123 });
 *
 * // Se categoriaId mudar rapidamente, o debounce evita requisições múltiplas
 * ```
 */
export function useQualities(options: UseQualitiesOptions): UseQualitiesResult {
	const { categoriaId, disableDebounce = false } = options;

	// Estado para categoriaId com debounce
	const [debouncedCategoriaId, setDebouncedCategoriaId] = useState<
		number | null
	>(categoriaId);
	const debounceTimeoutRef = useRef<number | null>(null);

	// Aplicar debounce quando categoriaId mudar
	useEffect(() => {
		if (disableDebounce) {
			setDebouncedCategoriaId(categoriaId);
			return;
		}

		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current);
		}

		debounceTimeoutRef.current = window.setTimeout(() => {
			setDebouncedCategoriaId(categoriaId);
		}, DEBOUNCE_MS);

		return () => {
			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
			}
		};
	}, [categoriaId, disableDebounce]);

	// Construir endpoint e chave de cache baseados na categoria
	const endpoint = useMemo(() => {
		if (!debouncedCategoriaId || debouncedCategoriaId <= 0) {
			return null;
		}
		return `${QUALITIES_ENDPOINT_BASE}/${debouncedCategoriaId}`;
	}, [debouncedCategoriaId]);

	const cacheKey = useMemo(() => {
		if (!debouncedCategoriaId || debouncedCategoriaId <= 0) {
			return null;
		}
		return `qualities-${debouncedCategoriaId}`;
	}, [debouncedCategoriaId]);

	// Carregar qualidades apenas se temos categoriaId válido
	const { data, loading, error, errorCode, refresh, hasAttempted } =
		useApiCache<Quality[]>(cacheKey || "qualities-empty", endpoint || "", {
			ttlMs: CACHE_TTL_MS,
			lazy: !endpoint, // Não carregar se não temos endpoint válido
			validate: (data) => {
				// Validar que é um array
				if (!Array.isArray(data)) {
					return false;
				}
				return true;
			},
		});

	// Retornar array vazio se não temos categoriaId válido
	const qualities = useMemo(() => {
		if (!debouncedCategoriaId || debouncedCategoriaId <= 0) {
			return [];
		}
		if (!data || !Array.isArray(data)) {
			return [];
		}
		return data;
	}, [data, debouncedCategoriaId]);

	return {
		qualities,
		loading: loading && !!debouncedCategoriaId,
		error: !debouncedCategoriaId ? null : error,
		errorCode: !debouncedCategoriaId ? null : errorCode,
		refresh,
		hasAttempted: hasAttempted && !!debouncedCategoriaId,
	};
}
